
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

interface Product {
  name: string;
  description: string;
  image: string;
  link: string;
  icon: string;
}

interface ProductCarouselProps {
  products: Product[];
  inView: boolean;
}

const ProductCarousel = ({ products, inView }: ProductCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalSlides = products.length;

  const startAutoPlay = useCallback(() => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % totalSlides);
    }, 4000);
  }, [totalSlides]);

  const stopAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isAutoPlaying && inView) {
      startAutoPlay();
    }
    return () => stopAutoPlay();
  }, [isAutoPlaying, inView, startAutoPlay, stopAutoPlay]);

  const goTo = (index: number) => {
    setActiveIndex(index);
    setIsAutoPlaying(false);
    stopAutoPlay();
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const goNext = () => goTo((activeIndex + 1) % totalSlides);
  const goPrev = () => goTo((activeIndex - 1 + totalSlides) % totalSlides);

  const handleDragStart = (clientX: number) => {
    setIsDragging(true);
    setDragStartX(clientX);
    setDragOffset(0);
    stopAutoPlay();
  };

  const handleDragMove = (clientX: number) => {
    if (!isDragging) return;
    setDragOffset(clientX - dragStartX);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragOffset < -60) {
      goNext();
    } else if (dragOffset > 60) {
      goPrev();
    }
    setDragOffset(0);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const getCardStyle = (index: number) => {
    let diff = index - activeIndex;
    if (diff > totalSlides / 2) diff -= totalSlides;
    if (diff < -totalSlides / 2) diff += totalSlides;

    const isActive = diff === 0;
    const isLeft = diff === -1;
    const isRight = diff === 1;
    const isFarLeft = diff <= -2;
    const isFarRight = diff >= 2;

    let x = 0;
    let scale = 0.65;
    let opacity = 0;
    let zIndex = 0;
    let rotateY = 0;

    if (isActive) {
      x = 0 + (isDragging ? dragOffset * 0.5 : 0);
      scale = 1;
      opacity = 1;
      zIndex = 30;
      rotateY = 0;
    } else if (isLeft) {
      x = -380 + (isDragging ? dragOffset * 0.3 : 0);
      scale = 0.82;
      opacity = 0.6;
      zIndex = 20;
      rotateY = 8;
    } else if (isRight) {
      x = 380 + (isDragging ? dragOffset * 0.3 : 0);
      scale = 0.82;
      opacity = 0.6;
      zIndex = 20;
      rotateY = -8;
    } else if (isFarLeft) {
      x = -650;
      scale = 0.65;
      opacity = 0;
      zIndex = 10;
      rotateY = 15;
    } else if (isFarRight) {
      x = 650;
      scale = 0.65;
      opacity = 0;
      zIndex = 10;
      rotateY = -15;
    }

    return { x, scale, opacity, zIndex, rotateY };
  };

  return (
    <div className="relative">
      {/* Carousel Container */}
      <div
        ref={containerRef}
        className="relative h-[520px] sm:h-[560px] lg:h-[600px] flex items-center justify-center select-none"
        style={{ perspective: '1200px' }}
        onMouseDown={(e) => handleDragStart(e.clientX)}
        onMouseMove={(e) => handleDragMove(e.clientX)}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
        onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
        onTouchEnd={handleDragEnd}
      >
        {products.map((product, index) => {
          const style = getCardStyle(index);
          return (
            <motion.div
              key={index}
              className="absolute w-[320px] sm:w-[360px] lg:w-[400px]"
              animate={{
                x: style.x,
                scale: style.scale,
                opacity: style.opacity,
                rotateY: style.rotateY,
              }}
              transition={{
                type: 'spring',
                stiffness: 260,
                damping: 30,
                mass: 0.8,
              }}
              style={{
                zIndex: style.zIndex,
                transformStyle: 'preserve-3d',
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: index * 0.12 }}
              >
                <div
                  className={`bg-white rounded-3xl shadow-lg overflow-hidden border transition-all duration-500 ${
                    index === activeIndex
                      ? 'border-teal/30 shadow-2xl shadow-teal/10'
                      : 'border-gray-100'
                  }`}
                >
                  {/* Image Area */}
                  <div className="relative w-full h-[240px] sm:h-[260px] lg:h-[280px] bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-6 sm:p-8 overflow-hidden group">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-br from-teal/5 to-green/5"
                      animate={
                        index === activeIndex
                          ? { opacity: [0.3, 0.6, 0.3] }
                          : { opacity: 0.1 }
                      }
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    <motion.img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain relative z-10"
                      animate={
                        index === activeIndex
                          ? { scale: [1, 1.05, 1], y: [0, -6, 0] }
                          : { scale: 1, y: 0 }
                      }
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    {/* Floating Icon */}
                    <motion.div
                      className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center bg-white rounded-full shadow-lg"
                      animate={
                        index === activeIndex
                          ? { scale: [1, 1.15, 1], rotate: [0, 10, 0] }
                          : { scale: 0.8, opacity: 0.4 }
                      }
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    >
                      <i className={`${product.icon} text-teal text-xl`} />
                    </motion.div>

                    {/* Shimmer Effect */}
                    {index === activeIndex && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                        animate={{ x: ['-200%', '200%'] }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          repeatDelay: 2,
                          ease: 'easeInOut',
                        }}
                      />
                    )}
                  </div>

                  {/* Content Area */}
                  <div className="p-6 sm:p-8">
                    <motion.h3
                      className="text-xl sm:text-2xl font-bold text-navy mb-3"
                      animate={
                        index === activeIndex
                          ? { x: 0, opacity: 1 }
                          : { x: 0, opacity: 0.7 }
                      }
                    >
                      {product.name}
                    </motion.h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-5 line-clamp-2">
                      {product.description}
                    </p>
                    <Link
                      to={product.link}
                      className="inline-flex items-center gap-2 text-teal text-sm font-semibold hover:gap-4 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      onClick={(e) => {
                        if (index !== activeIndex) {
                          e.preventDefault();
                          goTo(index);
                        }
                      }}
                    >
                      Detaylı İncele
                      <motion.i
                        className="ri-arrow-right-line"
                        animate={
                          index === activeIndex
                            ? { x: [0, 5, 0] }
                            : { x: 0 }
                        }
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      />
                    </Link>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <div className="flex items-center justify-center gap-6 mt-4">
        <button
          onClick={goPrev}
          className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-md hover:shadow-xl hover:border-teal/40 hover:bg-teal hover:text-white text-navy transition-all duration-300 cursor-pointer group"
          aria-label="Önceki ürün"
        >
          <i className="ri-arrow-left-s-line text-xl group-hover:scale-110 transition-transform" />
        </button>

        {/* Dots */}
        <div className="flex items-center gap-3">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className="relative cursor-pointer p-1"
              aria-label={`Ürün ${index + 1}`}
            >
              <motion.div
                className="rounded-full"
                animate={{
                  width: index === activeIndex ? 32 : 10,
                  height: 10,
                  backgroundColor:
                    index === activeIndex ? '#2c5f6f' : '#d1d5db',
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              />
              {index === activeIndex && (
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-teal/30"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1.4, opacity: [0.5, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeOut',
                  }}
                />
              )}
            </button>
          ))}
        </div>

        <button
          onClick={goNext}
          className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-md hover:shadow-xl hover:border-teal/40 hover:bg-teal hover:text-white text-navy transition-all duration-300 cursor-pointer group"
          aria-label="Sonraki ürün"
        >
          <i className="ri-arrow-right-s-line text-xl group-hover:scale-110 transition-transform" />
        </button>
      </div>

      {/* Product Name Indicator */}
      <div className="text-center mt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2 bg-navy/5 rounded-full"
          >
            <i className={`${products[activeIndex].icon} text-teal text-sm`} />
            <span className="text-navy text-sm font-semibold">
              {activeIndex + 1} / {totalSlides} — {products[activeIndex].name}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProductCarousel;
